/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Fetches live NFL game scores from ESPN public API.
 * No auth required — direct browser fetch.
 */

export interface NflGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: string;
  awayScore: string;
  homeRecord: string;
  awayRecord: string;
  /** e.g. "Q3 7:12", "Halftime", "Final", "8:15 PM" */
  statusDetail: string;
  /** 'pre' | 'in' | 'post' */
  state: string;
  homeLogo: string;
  awayLogo: string;
  venue: string;
  /** ISO date string for scheduled kickoff */
  startTime: string;
  /** NFL week number (1–18 regular season, 19+ playoffs) */
  week: number | null;
}

const ESPN_URL = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard';
const CACHE_KEY = 'finsuite-nfl-scores';
const CACHE_TTL_MS = 60 * 1000; // 1 minute for live scores

interface EspnCompetitor {
  homeAway?: string;
  team?: { abbreviation?: string; logo?: string } | null;
  score?: string | null;
  records?: Array<{ summary?: string }>;
}

interface EspnCompetition {
  competitors: EspnCompetitor[];
  status?: { type?: { shortDetail?: string; detail?: string; state?: string } };
  venue?: { fullName?: string };
}

interface EspnEvent {
  id: string;
  date?: string;
  week?: { number?: number };
  competitions: EspnCompetition[];
}

function getCached(): NflGame[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

export async function fetchNflGames(): Promise<NflGame[]> {
  const cached = getCached();
  if (cached) return cached;

  try {
    const res = await fetch(ESPN_URL);
    if (!res.ok) return [];
    const json = await res.json();
    const events: EspnEvent[] = json.events || [];

    // Guard against events with missing competitions/competitors (postponed, placeholder rows).
    const games: NflGame[] = events
      .filter((evt) => evt.competitions?.[0]?.competitors?.length)
      .map((evt) => {
        const comp = evt.competitions[0];
        const competitors = comp.competitors || [];
        const home = competitors.find((c) => c.homeAway === 'home');
        const away = competitors.find((c) => c.homeAway === 'away');
        return {
          id: evt.id,
          homeTeam: home?.team?.abbreviation || '?',
          awayTeam: away?.team?.abbreviation || '?',
          homeScore: home?.score || '0',
          awayScore: away?.score || '0',
          homeRecord: home?.records?.[0]?.summary || '',
          awayRecord: away?.records?.[0]?.summary || '',
          statusDetail: comp.status?.type?.shortDetail || comp.status?.type?.detail || '',
          state: comp.status?.type?.state || 'pre',
          homeLogo: home?.team?.logo || '',
          awayLogo: away?.team?.logo || '',
          venue: comp.venue?.fullName || '',
          startTime: evt.date || '',
          week: evt.week?.number ?? null
        };
      });

    // Sort: live first, then upcoming, then finished
    const sortOrder: Record<string, number> = { in: 0, pre: 1, post: 2 };
    games.sort((a, b) => (sortOrder[a.state] ?? 9) - (sortOrder[b.state] ?? 9));

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: games, ts: Date.now() }));
    } catch {
      // Ignore storage errors — data is still returned below
    }
    return games;
  } catch {
    return [];
  }
}
